import { ref, onBeforeUnmount } from 'vue'
import { queryProductTypeVariants } from '@/modules/catalog/api/catalog.service'

export function useProductVariantConfigurator() {
  const selectedValues = ref({})
  const variantAttributes = ref([])
  const matchingProductsCount = ref(null)
  const selectedProduct = ref(null)
  const defaultProduct = ref(null)
  const loading = ref(false)
  const error = ref(null)
  let productTypeId = null
  let requestVersion = 0
  let initializationGeneration = 0

  function reset() {
    ++requestVersion
    ++initializationGeneration
    productTypeId = null
    selectedValues.value = {}
    variantAttributes.value = []
    matchingProductsCount.value = null
    selectedProduct.value = null
    defaultProduct.value = null
    loading.value = false
    error.value = null
  }

  async function queryAvailability(resolveProduct = true) {
    if (!productTypeId) return null
    const version = ++requestVersion
    const typeId = productTypeId
    const selections = Object.entries(selectedValues.value).map(([attributeId, valueId]) => ({
      attributeId: Number(attributeId), valueId
    }))
    loading.value = true
    error.value = null
    selectedProduct.value = null
    matchingProductsCount.value = null
    try {
      const response = await queryProductTypeVariants(typeId, selections)
      if (version !== requestVersion) return null
      variantAttributes.value = response.variantAttributes ?? response.VariantAttributes ?? []
      matchingProductsCount.value = response.matchingProductsCount ?? response.MatchingProductsCount ?? 0
      defaultProduct.value = response.defaultProduct ?? response.DefaultProduct ?? null
      selectedProduct.value = resolveProduct ? response.selectedProduct ?? response.SelectedProduct ?? null : null
      return response
    } catch (failure) {
      if (version === requestVersion) error.value = failure
      return null
    } finally {
      if (version === requestVersion) loading.value = false
    }
  }

  async function initialize(typeId, requestedProduct = null) {
    reset()
    const generation = initializationGeneration
    const intendedTypeId = Number(typeId)
    const isCurrent = () => generation === initializationGeneration && productTypeId === intendedTypeId
    productTypeId = intendedTypeId
    const response = await queryAvailability(false)
    if (!isCurrent() || !response) return
    const initial = requestedProduct ?? defaultProduct.value
    const values = initial?.variantValues ?? initial?.VariantValues ?? []
    const next = {}
    for (const value of values) {
      const attributeId = Number(value.attributeId ?? value.AttributeId)
      const valueId = Number(value.valueId ?? value.ValueId)
      if (Number.isInteger(attributeId) && attributeId > 0 && Number.isInteger(valueId) && valueId > 0) {
        next[attributeId] = valueId
      }
    }
    selectedValues.value = next
    await queryAvailability()
    if (!isCurrent()) return
  }

  function selectValue(attributeId, valueId) {
    const attribute = variantAttributes.value.find(item => Number(item.attributeId ?? item.AttributeId) === Number(attributeId))
    const value = (attribute?.values ?? attribute?.Values ?? []).find(item => Number(item.valueId ?? item.ValueId) === Number(valueId))
    if (!value || (value.available ?? value.Available) !== true) return
    selectedValues.value = { ...selectedValues.value, [Number(attributeId)]: Number(valueId) }
    return queryAvailability()
  }

  function clearSelection(attributeId) {
    const next = { ...selectedValues.value }
    delete next[attributeId]
    selectedValues.value = next
    return queryAvailability()
  }

  function clearSelections() {
    selectedValues.value = {}
    return queryAvailability()
  }

  onBeforeUnmount(reset)
  return { selectedValues, variantAttributes, matchingProductsCount, selectedProduct, defaultProduct,
    loading, error, initialize, selectValue, clearSelection, clearSelections, queryAvailability, reset }
}
